import { LoginForm } from './LoginForm'
import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { authService, type AdminOnboardingResult } from '../../services/auth'
import { availableIndustryTypes, type IndustryType } from '../../domain/industryPresets'
import { useI18n } from '../../i18n/I18nContext'

interface SignupFormProps {
  onSuccess?: () => void
}

type OrganizationChoice = 'agency' | 'company' | ''
type OnboardingMode = 'create' | 'join'
type FieldErrors = Record<string, string>
type SignupAttribution = {
  referralCode?: string | null
  referralClickId?: string | null
  inviteCode?: string | null
  utmSource?: string | null
  utmMedium?: string | null
  utmCampaign?: string | null
}

const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
]

const nextSteps = [
  { title: 'Add first location', detail: 'Create the worksite or branch workers will punch into.' },
  { title: 'Invite workers', detail: 'Bring in employees, contractors, or agency staff.' },
  { title: 'Create job/order', detail: 'Post the first shift or labor request.' },
  { title: 'Assign worker', detail: 'Match a worker to the job assignment.' },
  { title: 'Test punch', detail: 'Confirm the mobile punch flow before the first live shift.' },
]

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

const attributionStorageKey = 'punch.signupAttribution'

const cleanParam = (value: string | null) => {
  const trimmed = String(value || '').trim()
  return trimmed || null
}

export const SignupForm: React.FC<SignupFormProps> = ({ onSuccess }) => {
  const { registerAdminOnboarding, completeAdminOnboarding } = useAuth()
  const { t } = useI18n()
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [industryType, setIndustryType] = useState<IndustryType | ''>('')
  const [organizationType, setOrganizationType] = useState<OrganizationChoice>('')
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>('create')
  const [organizationName, setOrganizationName] = useState('')
  const [slug, setSlug] = useState('')
  const [businessEmail, setBusinessEmail] = useState('')
  const [businessPhone, setBusinessPhone] = useState('')
  const [timezone, setTimezone] = useState('America/New_York')
  const [address, setAddress] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<AdminOnboardingResult | null>(null)
  const [attribution, setAttribution] = useState<SignupAttribution | null>(null)
  const [referralApplied, setReferralApplied] = useState(false)

  const organizationLabel = organizationType === 'agency' ? t('Agency') : organizationType === 'company' ? t('Company') : t('Organization')
  const successOrganization = result?.accessContext?.visibleOrganizations?.find((org) => org.id === result.onboarding?.organizationId)
    || result?.organization
  const canSubmit = step === 3 && !loading
  const primaryCta = useMemo(() => {
    if (step === 1) return t('Continue to organization type')
    if (step === 2) return t('Continue to organization details')
    if (step === 3) return loading ? t('Creating admin workspace...') : t('Create workspace and show next steps')
    return t('Open admin workspace')
  }, [loading, step, t])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fromUrl: SignupAttribution = {
      referralCode: cleanParam(params.get('ref')) || cleanParam(params.get('partner')),
      inviteCode: cleanParam(params.get('invite')) || cleanParam(params.get('sales_invite')),
      utmSource: cleanParam(params.get('utm_source')),
      utmMedium: cleanParam(params.get('utm_medium')),
      utmCampaign: cleanParam(params.get('utm_campaign')),
    }
    const hasUrlAttribution = Object.values(fromUrl).some(Boolean)
    let next = fromUrl
    if (!hasUrlAttribution) {
      try {
        next = JSON.parse(window.sessionStorage.getItem(attributionStorageKey) || window.localStorage.getItem(attributionStorageKey) || 'null') || {}
      } catch {
        next = {}
      }
    }
    if (!Object.values(next).some(Boolean)) return
    setAttribution(next)
    try {
      window.sessionStorage.setItem(attributionStorageKey, JSON.stringify(next))
      window.localStorage.setItem(attributionStorageKey, JSON.stringify(next))
    } catch {
      // Storage can be unavailable in private modes; signup still carries in-memory attribution.
    }
    if (next.referralCode) {
      authService.resolveReferralCode(next.referralCode)
        .then((response) => {
          if (response.data?.referral?.valid) {
            setReferralApplied(true)
            return authService.captureReferralClick({
              code: next.referralCode,
              landingUrl: window.location.href,
              referrerUrl: document.referrer || null,
              visitorId: window.localStorage.getItem('punch.visitorId') || undefined,
              utmSource: next.utmSource,
              utmMedium: next.utmMedium,
              utmCampaign: next.utmCampaign,
            })
          }
          return null
        })
        .then((response) => {
          const clickId = response?.data?.click?.id
          if (!clickId) return
          setAttribution((current) => {
            const updated = { ...(current || next), referralClickId: clickId }
            try {
              window.sessionStorage.setItem(attributionStorageKey, JSON.stringify(updated))
            } catch {
              // Non-fatal.
            }
            return updated
          })
        })
        .catch(() => setReferralApplied(false))
    } else if (next.inviteCode || next.utmSource || next.utmCampaign) {
      setReferralApplied(true)
    }
  }, [])

  const setOrgNameAndMaybeSlug = (value: string) => {
    setOrganizationName(value)
    if (!slug.trim()) setSlug(slugify(value))
  }

  const validateStep = (targetStep = step) => {
    const nextErrors: FieldErrors = {}
    if (targetStep === 1) {
      if (!name.trim()) nextErrors.name = t('Enter your full name.')
      if (!email.trim()) nextErrors.email = t('Enter your email.')
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) nextErrors.email = t('Enter a valid email address.')
      if (password.length < 8) nextErrors.password = t('Use at least 8 characters.')
      if (password !== confirmPassword) nextErrors.confirmPassword = t('Passwords must match.')
    }
    if (targetStep === 2 && !organizationType) {
      nextErrors.organizationType = t('Choose Agency or Company.')
    }
    if (targetStep === 3) {
      if (onboardingMode === 'join') {
        if (!businessEmail.trim()) nextErrors.businessEmail = t('Enter your organization email so an admin can invite you.')
        if (businessEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessEmail.trim())) {
          nextErrors.businessEmail = t('Enter a valid business email.')
        }
        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
      }
      if (!organizationName.trim()) nextErrors.organizationName = t('Enter the organization name.')
      if (!slug.trim()) nextErrors.slug = t('Enter a short URL-friendly slug.')
      if (slug.trim() && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
        nextErrors.slug = t('Use lowercase letters, numbers, and hyphens only.')
      }
      if (businessEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessEmail.trim())) {
        nextErrors.businessEmail = t('Enter a valid business email.')
      }
      if (!timezone.trim()) nextErrors.timezone = t('Choose a timezone.')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const goNext = () => {
    setSubmitError('')
    if (!validateStep(step)) return
    setStep((current) => Math.min(current + 1, 4))
  }

  const goBack = () => {
    setSubmitError('')
    setErrors({})
    setStep((current) => Math.max(current - 1, 1))
  }

  const submitOnboarding = async () => {
    if (!validateStep(3) || !organizationType) return
    if (onboardingMode === 'join') {
      setSubmitError(t('Join requests need an invitation from an existing organization admin. Ask them to use Roles & Access > Invite member.'))
      return
    }
    setLoading(true)
    setSubmitError('')
    try {
      const response = await registerAdminOnboarding({
        name: name.trim(),
        email: email.trim(),
        password,
        phone: phone.trim() || undefined,
        industryType: industryType || null,
        referralAttribution: attribution,
        organization: {
          type: organizationType,
          name: organizationName.trim(),
          slug: slugify(slug),
          businessEmail: businessEmail.trim() || undefined,
          businessPhone: businessPhone.trim() || undefined,
          timezone,
          address: address.trim() || undefined,
        },
      })
      setResult(response)
      setStep(4)
    } catch (err: any) {
      const reasonCode = err.response?.data?.reasonCode
      const existing = err.response?.data?.existingOrganization
      setSubmitError(
        reasonCode === 'ORGANIZATION_ALREADY_EXISTS' && existing?.name
          ? t('{name} already exists. Choose Join existing organization or ask an admin there to invite you.', { name: existing.name })
          : err.response?.data?.message || t('Unable to create the admin workspace.'),
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (step < 3) {
      goNext()
      return
    }
    if (step === 3) await submitOnboarding()
    if (step === 4 && result) {
      completeAdminOnboarding(result)
      onSuccess?.()
    }
  }

  if (result?.restricted || result?.authenticationState === 'mfa_enrollment_required') {
    return <div className="stack"><p>{t('Workspace created')}</p><LoginForm onSuccess={onSuccess} /></div>
  }

  return (
    <form onSubmit={handleSubmit} className="stack signup-wizard" data-testid="signup-form">
      <div className="signup-steps" aria-label={t('Signup progress')}>
        {['Account', 'Type', 'Details', 'Next'].map((label, index) => (
          <span key={label} className={`signup-step ${step === index + 1 ? 'active' : ''} ${step > index + 1 ? 'complete' : ''}`}>
            {t(label)}
          </span>
        ))}
      </div>
      {referralApplied ? <p className="success-text">{t('Referral applied.')}</p> : null}

      {step === 1 ? (
        <div className="stack" data-testid="signup-step-account">
          <div className="form-row">
            <label htmlFor="signupName">{t('Name')}</label>
            <input id="signupName" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} className="input" placeholder="Jordan Lee" />
            {errors.name ? <p className="error-text">{errors.name}</p> : null}
          </div>
          <div className="form-row">
            <label htmlFor="signupEmail">{t('Email')}</label>
            <input id="signupEmail" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className="input" placeholder="you@example.com" />
            <p className="helper">{t('Use the email you will use to administer this account.')}</p>
            {errors.email ? <p className="error-text">{errors.email}</p> : null}
          </div>
          <div className="admin-two-column">
            <div className="form-row">
              <label htmlFor="signupPassword">{t('Password')}</label>
              <input id="signupPassword" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} className="input" placeholder={t('Minimum 8 characters')} />
              {errors.password ? <p className="error-text">{errors.password}</p> : null}
            </div>
            <div className="form-row">
              <label htmlFor="signupConfirm">{t('Confirm password')}</label>
              <input id="signupConfirm" type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="input" placeholder={t('Re-enter password')} />
              {errors.confirmPassword ? <p className="error-text">{errors.confirmPassword}</p> : null}
            </div>
          </div>
          <div className="form-row">
            <label htmlFor="signupPhone">{t('Phone optional')}</label>
            <input id="signupPhone" type="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} className="input" placeholder="(555) 123-4567" />
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="stack" data-testid="signup-step-organization-type">
          <div className="signup-choice-grid" role="radiogroup" aria-label={t('Organization type')}>
            <button type="button" className={`signup-choice ${organizationType === 'agency' ? 'active' : ''}`} onClick={() => setOrganizationType('agency')} data-testid="signup-type-agency">
              <strong>{t('Agency')}</strong>
              <span>{t('Supplies/manages workers.')}</span>
            </button>
            <button type="button" className={`signup-choice ${organizationType === 'company' ? 'active' : ''}`} onClick={() => setOrganizationType('company')} data-testid="signup-type-company">
              <strong>{t('Company')}</strong>
              <span>{t('Owns work locations or requests labor.')}</span>
            </button>
          </div>
          <div className="stack" style={{ gap: '.5rem' }}>
            <div>
              <strong>{t('What best describes your organization?')}</strong>
              <p className="helper">{t('This adjusts labels, dashboard cards, setup copy, and sales/marketing presets. You can change it later from Platform Admin controls.')}</p>
            </div>
            <select
              className="input"
              value={industryType}
              onChange={(event) => setIndustryType(event.target.value as IndustryType | '')}
              data-testid="signup-industry-selector"
            >
              <option value="">{t('General Workforce Operations')}</option>
              {availableIndustryTypes
                .filter((industry) => industry.key !== 'general_workforce')
                .map((industry) => (
                  <option key={industry.key} value={industry.key}>{t(industry.displayName)}</option>
                ))}
            </select>
          </div>
          <div className="signup-choice-grid" role="radiogroup" aria-label={t('Organization onboarding mode')}>
            <button type="button" className={`signup-choice ${onboardingMode === 'create' ? 'active' : ''}`} onClick={() => setOnboardingMode('create')}>
              <strong>{t('Create new organization')}</strong>
              <span>{t('Use this only when your agency or company is not already in this tenant.')}</span>
            </button>
            <button type="button" className={`signup-choice ${onboardingMode === 'join' ? 'active' : ''}`} onClick={() => setOnboardingMode('join')}>
              <strong>{t('Join existing organization')}</strong>
              <span>{t('Have an admin invite you to the current organization or OU.')}</span>
            </button>
          </div>
          {errors.organizationType ? <p className="error-text">{errors.organizationType}</p> : null}
        </div>
      ) : null}

      {step === 3 ? (
        <div className="stack" data-testid="signup-step-organization-details">
          <p className="helper">
            {onboardingMode === 'join'
              ? t('Existing organizations are joined by invitation so access stays tenant, organization, OU, and location scoped.')
              : t('{organization} setup creates your first organization context so the dashboard has useful next steps.', { organization: organizationLabel })}
          </p>
          {onboardingMode === 'join' ? (
            <div className="admin-disabled-callout">{t('Ask an admin in the existing organization to send an invite from Roles & Access. This prevents duplicate agency/company records.')}</div>
          ) : null}
          <div className="form-row">
            <label htmlFor="organizationName">{t('Organization name')}</label>
            <input id="organizationName" value={organizationName} onChange={(event) => setOrgNameAndMaybeSlug(event.target.value)} className="input" placeholder="North Star Staffing" />
            {errors.organizationName ? <p className="error-text">{errors.organizationName}</p> : null}
          </div>
          <div className="form-row">
            <label htmlFor="organizationSlug">{t('Slug')}</label>
            <input id="organizationSlug" value={slug} onChange={(event) => setSlug(slugify(event.target.value))} className="input" placeholder="north-star-staffing" />
            <p className="helper">{t('Lowercase letters, numbers, and hyphens.')}</p>
            {errors.slug ? <p className="error-text">{errors.slug}</p> : null}
          </div>
          <div className="admin-two-column">
            <div className="form-row">
              <label htmlFor="businessEmail">{t('Business email optional')}</label>
              <input id="businessEmail" type="email" value={businessEmail} onChange={(event) => setBusinessEmail(event.target.value)} className="input" placeholder="ops@example.com" />
              {errors.businessEmail ? <p className="error-text">{errors.businessEmail}</p> : null}
            </div>
            <div className="form-row">
              <label htmlFor="businessPhone">{t('Business phone optional')}</label>
              <input id="businessPhone" type="tel" value={businessPhone} onChange={(event) => setBusinessPhone(event.target.value)} className="input" placeholder="(555) 987-6543" />
            </div>
          </div>
          <div className="form-row">
            <label htmlFor="timezone">{t('Timezone')}</label>
            <select id="timezone" className="input" value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              {timezones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
            </select>
            {errors.timezone ? <p className="error-text">{errors.timezone}</p> : null}
          </div>
          <div className="form-row">
            <label htmlFor="businessAddress">{t('Address optional')}</label>
            <textarea id="businessAddress" value={address} onChange={(event) => setAddress(event.target.value)} className="input" rows={3} placeholder={t('Street, city, state, postal code')} />
          </div>
        </div>
      ) : null}

      {step === 4 && result ? (
        <div className="stack" data-testid="signup-step-success">
          <div className="signup-success">
            <span className="status-pill status-pill--confirmed">{t('Workspace created')}</span>
            <h2 className="heading-lg">{successOrganization?.name || organizationName}</h2>
            <p className="text-muted">
              {result.accessContext
                ? t('Your admin context is ready.')
                : t('Your organization was created. Access context is still warming up, so the workspace will use the registration response first.')}
            </p>
            {result.accessContextError ? <p className="helper">{result.accessContextError}</p> : null}
            {result.onboarding?.signupAttribution ? <p className="success-text">{t('Referral attribution was attached to this workspace.')}</p> : null}
          </div>
          <div className="signup-next-grid">
            {nextSteps.map((item) => (
              <div className="signup-next-card" key={item.title}>
                <strong>{t(item.title)}</strong>
                <span>{t(item.detail)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {submitError ? <p className="error-text">{submitError}</p> : null}

      <div className="signup-actions">
        {step > 1 && step < 4 ? (
          <button type="button" className="btn btn-secondary" onClick={goBack}>
            {t('Back')}
          </button>
        ) : null}
        <button type="submit" disabled={!canSubmit && step === 3} className="btn btn-primary" data-testid="signup-primary">
          {primaryCta}
        </button>
      </div>
    </form>
  )
}
